import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import React from "react";

export const ContextMenuRoot = ContextMenuPrimitive.Root;
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
export const ContextMenuPortal = ContextMenuPrimitive.Portal;

export const ContextMenuSeparator = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.Separator
        ref={ref}
        className={cn("my-1 h-px bg-content-tertiary/20", className)}
        {...props}
    />
));
ContextMenuSeparator.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

export const ContextMenuContent = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.Content
        ref={ref}
        className={cn("min-w-[220px] rounded-md bg-surface-01 p-1 shadow-lg", className)}
        {...props}
    />
));
ContextMenuContent.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

import { cva } from "class-variance-authority";

const contextMenuItemVariants = cva(
    cn(
        "flex cursor-pointer items-center rounded px-2 py-1.5 text-sm outline-none hover:bg-surface-03 focus:bg-surface-03",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    ),
    {
        variants: {
            variant: {
                default: "",
                destructive: "text-red-500",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

export const ContextMenuItem = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.ContextMenuItem>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.ContextMenuItem> &
        PropsWithClassName & { variant?: "default" | "destructive" }
>(({ onClick, className, variant, ...props }, ref) => (
    <ContextMenuPrimitive.ContextMenuItem
        ref={ref}
        className={cn(contextMenuItemVariants({ variant }), className)}
        onClick={(e) => {
            if (onClick) {
                // We're using setTimeout to allow the context menu to clear its modification to body.style before triggering the onclick,
                // This is important if the onClick handler opens a modal, as otherwise the modal will reset the style when the modal is
                // closed, which causes the pointer-events:none to stick around.
                setTimeout(() => onClick(e), 0);
            }
        }}
        {...props}
    />
));
ContextMenuItem.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;
